<div class="p-5">
  <!-- Header -->
  <div class="flex justify-between items-center mb-5 gap-3">
    <div class="flex items-center gap-2 min-w-0">
      <button
        mat-icon-button
        (click)="navigateBack()"
        matTooltip="{{ 'email-logs.detail.back' | translate }}"
        [attr.aria-label]="'email-logs.detail.back' | translate">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <h1 class="text-2xl font-medium truncate">{{ detail()?.subject ?? ('email-logs.detail.title' | translate) }}</h1>
    </div>

    <div class="flex items-center gap-2 shrink-0">
      @if (detail()) {
        <!-- Tooltip lives on the wrapper: Material suppresses pointer events on disabled buttons. -->
        <span [matTooltip]="canResend() ? '' : ('email-logs.list.resend-blocked' | translate)">
          <button mat-flat-button color="primary" [disabled]="resending() || !canResend()" (click)="resend()">
            <mat-icon>send</mat-icon>
            {{ (resending() ? 'email-logs.detail.resending' : 'email-logs.detail.resend') | translate }}
          </button>
        </span>
      }
    </div>
  </div>

  @if (initialLoading()) {
    <div class="flex justify-center p-10">
      <mat-progress-spinner diameter="40" mode="indeterminate" />
    </div>
  } @else if (detail(); as log) {
    <!-- Resend of: this row is a copy of an earlier message -->
    @if (log.resendOfEmailMessageId) {
      <div
        class="mb-4 flex flex-wrap items-center gap-2 rounded border-l-4 border-gray-400 bg-gray-50 p-3 text-sm dark:border-gray-500 dark:bg-gray-900/40">
        <mat-icon class="!text-gray-600 dark:!text-gray-300">replay</mat-icon>
        <span class="text-gray-700 dark:text-gray-300">{{ 'email-logs.detail.resend-of' | translate }}</span>
        <a
          mat-button
          [routerLink]="emailLogLink(log.resendOfEmailMessageId)"
          [queryParams]="linkQueryParams()"
          class="!min-w-0">
          {{ 'email-logs.detail.view-original' | translate }}
        </a>
      </div>
    }

    <!-- Side-by-side: details/timeline/attachments left, body preview right (stacked below xl) -->
    <div class="grid grid-cols-1 xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)] gap-4 items-start">
    <div class="min-w-0">
    <!-- Metadata -->
    <mat-card class="mb-4" [class.border-l-4]="isProblem()" [class.border-red-500]="isProblem()">
      <mat-card-content>
        <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.logType' | translate }}
            </dt>
            <dd class="mt-0.5">
              <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200">
                {{ enumLabel('logType', log.logType) }}
              </span>
            </dd>
          </div>

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.status' | translate }}
            </dt>
            <dd class="mt-0.5 flex flex-wrap items-center gap-2">
              <span class="px-2 py-0.5 rounded text-xs font-medium" [class]="stateClass(log.status)">
                {{ enumLabel('status', log.status) }}
              </span>
              <span class="text-xs text-gray-500 dark:text-gray-400">
                {{ 'email-logs.field.attemptCount' | translate }}: {{ log.attemptCount }}
              </span>
            </dd>
          </div>

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.deliveryStatus' | translate }}
            </dt>
            <dd class="mt-0.5 flex flex-wrap items-center gap-2">
              @if (log.deliveryStatus) {
                <span class="px-2 py-0.5 rounded text-xs font-medium" [class]="stateClass(log.deliveryStatus)">
                  {{ enumLabel('deliveryStatus', log.deliveryStatus) }}
                </span>
                @if (log.deliveryStatusAt) {
                  <span class="text-xs text-gray-500 dark:text-gray-400">{{ log.deliveryStatusAt | date: 'medium' }}</span>
                }
              } @else {
                <span class="text-gray-500 dark:text-gray-400">{{ 'email-logs.detail.no-delivery-status' | translate }}</span>
              }
            </dd>
          </div>

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.detail.recipient' | translate }}
            </dt>
            <dd class="mt-0.5 break-words">
              @if (log.recipientEmail) {
                @if (log.toName) {
                  <span>{{ log.toName }}</span>
                  <span class="text-gray-500 dark:text-gray-400"> &lt;{{ log.recipientEmail }}&gt;</span>
                } @else {
                  {{ log.recipientEmail }}
                }
              } @else {
                <span class="text-gray-500 dark:text-gray-400">{{ 'email-logs.detail.no-recipient' | translate }}</span>
              }
            </dd>
          </div>

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.customer' | translate }}
            </dt>
            <dd class="mt-0.5">
              @if (log.customerId) {
                <a
                  class="text-primary-700 dark:text-primary-300 hover:underline"
                  [routerLink]="['/customers/edit', log.customerId]"
                  matTooltip="{{ 'email-logs.detail.open-customer' | translate }}">
                  {{ log.customerName ?? '#' + log.customerId }}
                </a>
              } @else {
                {{ log.customerName ?? '—' }}
              }
            </dd>
          </div>

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.reservation' | translate }}
            </dt>
            <dd class="mt-0.5">
              @if (log.reservationId) {
                <a
                  class="text-primary-700 dark:text-primary-300 hover:underline"
                  [routerLink]="['/reservations/edit', log.reservationId]"
                  matTooltip="{{ 'email-logs.detail.open-reservation' | translate }}">
                  {{ log.reservationNumber ?? '#' + log.reservationId }}
                </a>
              } @else {
                {{ log.reservationNumber ?? '—' }}
              }
            </dd>
          </div>

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.createdOn' | translate }}
            </dt>
            <dd class="mt-0.5">{{ log.createdOn | date: 'medium' }}</dd>
          </div>

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.acceptedAt' | translate }}
            </dt>
            <dd class="mt-0.5">
              @if (log.acceptedAt) {
                {{ log.acceptedAt | date: 'medium' }}
              } @else {
                <span class="text-gray-500 dark:text-gray-400">{{ 'email-logs.detail.not-sent-yet' | translate }}</span>
              }
            </dd>
          </div>

          @if (showNextAttempt()) {
            <div>
              <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ 'email-logs.detail.next-attempt' | translate }}
              </dt>
              <dd class="mt-0.5">{{ log.nextAttemptAt | date: 'medium' }}</dd>
            </div>
          }

          @if (log.providerMessageId) {
            <div>
              <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ 'email-logs.detail.provider-message-id' | translate }}
              </dt>
              <dd class="mt-0.5 font-mono text-xs break-all">{{ log.providerMessageId }}</dd>
            </div>
          }

          @if (log.dedupKey) {
            <div>
              <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ 'email-logs.detail.dedup-key' | translate }}
              </dt>
              <dd class="mt-0.5 font-mono text-xs break-all">{{ log.dedupKey }}</dd>
            </div>
          }

          <div>
            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ 'email-logs.field.publicId' | translate }}
            </dt>
            <dd class="mt-0.5 font-mono text-xs break-all">{{ log.publicId }}</dd>
          </div>
        </dl>

        <!-- Last failure reason; kept visible after a later success as history -->
        @if (log.lastError) {
          <div class="mt-4 border-l-4 border-red-400 bg-red-50 p-3 text-sm dark:border-red-500 dark:bg-red-950/40">
            <p class="m-0 mb-1 text-xs font-medium uppercase tracking-wider text-red-700 dark:text-red-300">
              {{ 'email-logs.detail.last-error' | translate }}
            </p>
            <p class="m-0 break-words text-red-700 dark:text-red-200">{{ log.lastError }}</p>
          </div>
        }
      </mat-card-content>
    </mat-card>

    <!-- Attachments -->
    @if (log.attachments.length > 0) {
      <mat-card class="mb-4">
        <mat-card-content>
          <h2 class="m-0 mb-3 text-base font-medium">{{ 'email-logs.detail.section.attachments' | translate }}</h2>
          <ul class="m-0 list-none p-0">
            @for (attachment of log.attachments; track attachment.id) {
              <li class="flex items-center gap-3 border-t border-gray-200 py-2 first:border-t-0 dark:border-gray-700">
                <mat-icon class="!text-gray-500 dark:!text-gray-400">attach_file</mat-icon>
                <div class="min-w-0 flex-1">
                  <p class="m-0 truncate font-medium">{{ attachment.fileName }}</p>
                  <p class="m-0 text-xs text-gray-500 dark:text-gray-400">
                    {{ attachment.contentType }} · {{ formatSize(attachment.sizeBytes) }}
                  </p>
                </div>
                @if (downloadingAttachmentId() === attachment.id) {
                  <mat-progress-spinner diameter="20" mode="indeterminate" />
                } @else {
                  <button
                    mat-icon-button
                    (click)="downloadAttachment(attachment)"
                    [disabled]="downloadingAttachmentId() !== null"
                    matTooltip="{{ 'email-logs.detail.download-attachment' | translate }}"
                    [attr.aria-label]="'email-logs.detail.download-attachment' | translate">
                    <mat-icon>download</mat-icon>
                  </button>
                }
              </li>
            }
          </ul>
        </mat-card-content>
      </mat-card>
    }

    <!-- Delivery timeline (oldest first, as returned by the API) -->
    <mat-card class="mb-4">
      <mat-card-content>
        <h2 class="m-0 mb-3 text-base font-medium">{{ 'email-logs.detail.section.timeline' | translate }}</h2>
        @if (log.events.length === 0) {
          <p class="m-0 text-sm text-gray-500 dark:text-gray-400">{{ 'email-logs.detail.no-events' | translate }}</p>
        } @else {
          <ol class="m-0 list-none p-0">
            @for (event of log.events; track event.id) {
              <li class="flex gap-3">
                <div class="flex flex-col items-center">
                  <span
                    class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    [class]="stateClass(event.eventType)">
                    <mat-icon class="!h-[18px] !w-[18px] !text-[18px] !leading-[18px]">
                      {{ eventIcon(event.eventType) }}
                    </mat-icon>
                  </span>
                  @if (!$last) {
                    <span class="my-1 w-px flex-1 bg-gray-200 dark:bg-gray-700"></span>
                  }
                </div>
                <div class="min-w-0 pb-4">
                  <p class="m-0 font-medium">{{ enumLabel('eventType', event.eventType) }}</p>
                  <p
                    class="m-0 text-sm text-gray-600 dark:text-gray-400"
                    matTooltip="{{ 'email-logs.detail.event-received' | translate }}: {{ event.receivedAt | date: 'medium' }}">
                    {{ event.occurredAt | date: 'medium' }}
                  </p>
                  @if (event.reason) {
                    <p class="m-0 mt-1 break-words text-sm text-red-700 dark:text-red-300">{{ event.reason }}</p>
                  }
                </div>
              </li>
            }
          </ol>
        }
      </mat-card-content>
    </mat-card>

    <!-- Template data (caller-supplied metadata, for debugging) -->
    @if (prettyTemplateData(); as templateData) {
      <mat-card class="mb-4">
        <mat-card-content>
          <div class="flex items-center justify-between gap-3">
            <h2 class="m-0 text-base font-medium">{{ 'email-logs.detail.section.template-data' | translate }}</h2>
            <button mat-button (click)="toggleTemplateData()">
              {{ (showTemplateData() ? 'email-logs.detail.hide' : 'email-logs.detail.show') | translate }}
            </button>
          </div>
          @if (showTemplateData()) {
            <pre class="mt-2 max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs dark:bg-gray-900/60">{{ templateData }}</pre>
          }
        </mat-card-content>
      </mat-card>
    }
    </div>

    <!-- Rendered body (isolated in a sandboxed iframe; scripts disabled). Sticky on wide screens so the
         preview stays in view while scrolling the details/timeline column. -->
    <div class="min-w-0 xl:sticky xl:top-4">
    <mat-card>
      @if (log.textBody) {
        <mat-card-content class="!pb-0">
          <div class="flex items-center justify-between gap-3">
            <h2 class="m-0 text-base font-medium">{{ 'email-logs.detail.body' | translate }}</h2>
            <mat-button-toggle-group
              [value]="bodyView()"
              (change)="toggleBodyView($event.value)"
              [hideSingleSelectionIndicator]="true"
              aria-label="{{ 'email-logs.detail.body' | translate }}">
              <mat-button-toggle value="html">{{ 'email-logs.detail.body-view-html' | translate }}</mat-button-toggle>
              <mat-button-toggle value="text">{{ 'email-logs.detail.body-view-text' | translate }}</mat-button-toggle>
            </mat-button-toggle-group>
          </div>
        </mat-card-content>
      }
      <mat-card-content class="!p-0">
        @if (bodyView() === 'text') {
          <pre class="m-0 max-h-[600px] xl:max-h-[80vh] overflow-auto p-4 text-sm whitespace-pre-wrap">{{ log.textBody }}</pre>
        } @else {
          <iframe
            class="w-full min-h-[600px] xl:min-h-[80vh] border-0 bg-white"
            sandbox
            [srcdoc]="safeBody()"
            [title]="'email-logs.detail.body' | translate"></iframe>
        }
      </mat-card-content>
    </mat-card>
    </div>
    </div>
  } @else if (loadFailed()) {
    <mat-card>
      <mat-card-content>
        <div class="p-6 text-center">
          <mat-icon class="!mb-4 !h-16 !w-16 !text-6xl !text-gray-400">mail_outline</mat-icon>
          <p class="text-base text-gray-700 dark:text-gray-300">{{ 'email-logs.detail.not-found' | translate }}</p>
          <button mat-stroked-button class="mt-2" (click)="navigateBack()">
            {{ 'email-logs.detail.back' | translate }}
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  }
</div>
